import React, {useEffect, useState} from "react";
import {changePageSizeState} from "../../../Slices/PageSlice";
import PageSizeState from "../../../Constants/Base/PageSizeState";
import {useDispatch} from "react-redux";
import {deleteStockChange, getStockChangeList} from "../../../Services/AssetManagement/StockChangeService";
import TablePage from "../../../Components/DataGrid/TablePage";
import strings from "../../../localization";
import { formatColumnObject, formatColumnDate } from "../../../Components/DataGrid/ValueFormatter";
import {changeBreadcrumbs} from "../../../Slices/BreadcrumbsSlice";
import Modules from "../../../Constants/Base/Modules";
import AddStockChange from "./AddStockChange";
import TablePageContext, {
    FilterDefaults,
    TableDataDefaults,
    TablePageOptionDefaults
} from "../../../Context/TablePageContext";
import { renderColumnNumber } from "../../../Components/DataGrid/ValueCellRender";
import PageState from "../../../Constants/Base/PageState";
import EditStockChange from "./EditStockChange";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReferenceType from "../../../Constants/DocumentManagement/ReferenceType";
import AppPermissions from "../../../Constants/Permissions/Permissions";
import Tag from "../../../Components/Forms/Pages/Document/Tag/Tag";

const tableDescription = [
    {
        field: 'type', headerName: strings.pages.assetManagement.stockChange.stockChangeList.type,
        width: 200,
    },
    {
        field: 'price',
        headerName: strings.pages.assetManagement.stockChange.stockChangeList.price,
        width: 200,
        renderCell: (params) => renderColumnNumber(params)
    },
    {
        field: 'tax', 
        headerName: strings.pages.assetManagement.stockChange.stockChangeList.tax,
        width: 200,
        renderCell: (params) => renderColumnNumber(params)
    },
    {
        field: 'quantity', headerName: strings.pages.assetManagement.stockChange.stockChangeList.quantity,
        width: 150,
    },
    {
        field: 'stockChangeDate',
        headerName: strings.pages.assetManagement.stockChange.stockChangeList.stockChangeDate,
        valueFormatter: (params) => formatColumnDate(params),
        width: 150
    },
    {
        field: 'inventory',
        headerName: strings.pages.assetManagement.stockChange.stockChangeList.inventory,
        valueFormatter: (params) => formatColumnObject(params, 'id'),
        width: 100
    }
];

const filters = []

const StockChangeList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [updatedFilter, setUpdatedFilter] = useState(!Boolean(searchParams.toString()));
    const [columns, setColumns] = useState([]);
    const [pageState, setPageState] = useState(PageState.View);
    const [filter, setFilter] = useState(FilterDefaults);
    const [tableData, setTableData] = useState(TableDataDefaults);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [selectedItems, setSelectedItems] = useState(null);
    const [tablePageOptions, setTablePageOptions] = useState(TablePageOptionDefaults);
    const referenceType = ReferenceType.STOCK_CHANGE;
    const permissionGroup = AppPermissions.STOCK_CHANGE.GROUP;

    const value = {
        tablePageOptions, setTablePageOptions,
        selectionModel, setSelectionModel,
        selectedItems, setSelectedItems,
        filter, setFilter, pageState, setPageState, showDeleteDialog, setShowDeleteDialog,
        navigate, updatedFilter, setUpdatedFilter
    }

    useEffect(() => {
        setColumns([...tableDescription]);
        dispatch(changePageSizeState(PageSizeState.SHORT));
        dispatch(changeBreadcrumbs({
            title: strings.pages.assetManagement.stockChange.stockChangeList.pageTitle,
            hierarchy:[
                {label: strings.navigation.managmentTag},
                {label: Modules.ASSETS},
                {label: strings.pages.assetManagement.stockChange.stockChangeList.pageTitle}
            ],        
        }));

        return () => {
            setTableData({})
        }
    }, [])

    useEffect(() => {
        if(!updatedFilter){
            return;
        }

        fetch();
    }, [filter, updatedFilter]);
    
    const onCancel = () => {
        setPageState(PageState.View);
    }

    const onFinish = () => {
        fetch();
        setPageState(PageState.View);
    }

    const fetch = () => {
        setTableData({
            ...tableData,
            loading: true
        });

        getStockChangeList({
            ...filter
        }).then(response => {

            if (!response || !response.ok) {
                return;
            }

            setTableData({
                loading: false,
                data: response.data.result,
                total: response.data.total
            });
        })
    }

    return <TablePageContext.Provider value={value}>
        <TablePage onFinish={() => onFinish()} deleteItem={deleteStockChange} tableDescription={columns}
                   tableData={tableData}
                   filter={filter} filters={filters}
                   referenceType={referenceType} permissionGroup={permissionGroup}
                   tagPage={<Tag referenceType={ referenceType } data={ selectedItems } /> }
                   editPage={<EditStockChange data={ selectedItems }
                   onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addPage={<AddStockChange onCancel={() => onCancel()} onFinish={() => onFinish()}/>}
                   addButtonText={strings.components.tablePage.addButton.stockChange}
                   />
    </TablePageContext.Provider>;
}

export default StockChangeList;
